import { CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getSubscriptionPlanOptions, type PlanInterval } from '@/lib/subscriptionPlan';
import { cn } from '@/lib/utils';
import { useSubscription } from '@/hooks/useSubscription';
import { useCheckout } from '../hooks/useCheckout';
import { useCustomerPortal } from '../hooks/useCustomerPortal';

interface PlanModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
}

const planFeatures = [
  'Unlimited areas and papers',
  'Full editor experience',
  'Activity and SenBar tracking',
  'Image uploads and storage',
];

export function PlanModal({
  open,
  onOpenChange,
  title = 'Choose a plan',
  description = 'Subscribe to keep writing, organizing, and uploading in Sen.',
}: PlanModalProps) {
  const planOptions = getSubscriptionPlanOptions();
  const { error, loadingInterval, startCheckout } = useCheckout();
  const { error: portalError, loading: portalLoading, openCustomerPortal } = useCustomerPortal();
  const { billingAction, currentPlanInterval, isTrialing } = useSubscription();

  const plans: Array<{
    interval: PlanInterval;
    label: string;
    badge?: string;
    priceLabel: string;
    description: string;
  }> = [
    {
      interval: 'month',
      label: 'Monthly',
      badge: 'Flexible',
      priceLabel: planOptions.monthly.priceLabel,
      description: 'Full access with a straightforward monthly plan.',
    },
    {
      interval: 'year',
      label: 'Yearly',
      badge: 'Best value',
      priceLabel: planOptions.yearly.priceLabel,
      description: 'One yearly payment with two months included.',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          {plans.map((plan) => {
            const isLoading = loadingInterval === plan.interval;
            const isCurrentPlan = currentPlanInterval === plan.interval;
            const usesPortal = billingAction === 'portal';
            const disablesCurrentPlan = isCurrentPlan && !isTrialing;
            const buttonLabel = usesPortal && isTrialing
              ? 'Manage billing'
              : isCurrentPlan
                ? 'Current plan'
                : usesPortal
                  ? `Switch to ${plan.label}`
                  : `Subscribe ${plan.label}`;
            const badgeLabel = isCurrentPlan
              ? isTrialing
                ? 'Trial plan'
                : 'Current plan'
              : plan.badge;
            const handlePlanAction = () => {
              if (disablesCurrentPlan) return;
              if (usesPortal) {
                void openCustomerPortal();
                return;
              }
              void startCheckout(plan.interval);
            };

            return (
              <div
                key={plan.interval}
                className={cn(
                  'flex flex-col rounded-lg border border-border bg-background p-4',
                  plan.interval === 'year' && 'border-primary/50 bg-primary/5'
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-base font-semibold text-foreground">{plan.label}</div>
                    <div className="mt-1 text-xs font-medium text-muted-foreground">
                      {planOptions.monthly.name}
                    </div>
                    <div className="mt-1 text-sm text-muted-foreground">{plan.description}</div>
                  </div>
                  {badgeLabel ? (
                    <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                      {badgeLabel}
                    </span>
                  ) : null}
                </div>

                <div className="mt-5 text-2xl font-semibold text-foreground">{plan.priceLabel}</div>

                <ul className="mt-5 flex-1 space-y-2 text-sm text-muted-foreground">
                  {planFeatures.map((feature) => (
                    <li key={feature} className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="mt-5 w-full"
                  onClick={handlePlanAction}
                  disabled={loadingInterval !== null || portalLoading || disablesCurrentPlan}
                  variant={plan.interval === 'year' ? 'default' : 'outline'}
                >
                  {isLoading || portalLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {buttonLabel}
                </Button>
              </div>
            );
          })}
        </div>

        {error || portalError ? (
          <Alert variant="destructive">
            <AlertDescription>{error || portalError}</AlertDescription>
          </Alert>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
